import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Schedule, HolidayResponse } from '../types';

import {
  getParentConnectSessions,
  type ParentConnectSessionRow,
} from './get-parent-connect-sessions';
import { getSchedule } from './get-schedule';
import { getStudentHolidays } from './get-student-holidays';

// Query Keys for Calendar
const calendarKeys = {
  all: ['calendar'] as const,
  schedule: () => [...calendarKeys.all, 'schedule'] as const,
  parentConnect: () => [...calendarKeys.all, 'parent-connect'] as const,
  holidays: (eventStatus: string, userId: string) =>
    [...calendarKeys.all, 'holidays', eventStatus, userId] as const,
} as const;

// Get Schedule Query
const useSchedule = () => {
  return useQuery<Schedule.Response>({
    queryKey: calendarKeys.schedule(),
    queryFn: getSchedule,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Get Parent-Connect (TPC) Sessions Query
const useParentConnectSessions = () => {
  return useQuery<ParentConnectSessionRow[]>({
    queryKey: calendarKeys.parentConnect(),
    queryFn: getParentConnectSessions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Get Student Holidays Query
const useStudentHolidays = (
  eventStatus: string,
  userId: string,
  enabled = true
) => {
  return useQuery<HolidayResponse>({
    queryKey: calendarKeys.holidays(eventStatus, userId),
    queryFn: () => getStudentHolidays(eventStatus, userId),
    staleTime: 24 * 60 * 60 * 1000, // 24 hours (holidays don't change often)
    gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    refetchOnWindowFocus: false,
    enabled: enabled && !!eventStatus && !!userId,
  });
};

// Schedule Hook
export const useScheduleData = () => {
  const { data, isLoading, isError, error, refetch } = useSchedule();

  const lessons = useMemo(
    () =>
      data?.lessons?.map(l => ({
        ...l,
        status: l.status
          ? (String(l.status).toLowerCase() as Schedule.Lesson['status'])
          : l.status,
        variant: l.variant
          ? (String(l.variant).toLowerCase() as Schedule.Lesson['variant'])
          : l.variant,
        joiningLink: l.joining_link ?? l.joiningLink,
      })) ?? [],
    [data?.lessons]
  );

  const pastLessons = useMemo(
    () =>
      lessons.filter(
        lesson =>
          lesson.status === 'attended' ||
          lesson.status === 'missed' ||
          lesson.status === 'cancelled'
      ),
    [lessons]
  );

  const upcomingLessons = useMemo(
    () =>
      lessons.filter(
        lesson => lesson.status === 'upcoming' || lesson.status === 'holiday'
      ),
    [lessons]
  );

  return {
    // Data
    lessons,
    pastLessons,
    upcomingLessons,
    totalLessons: lessons.length,
    timezone: data?.timezone,

    // API response meta
    status: data?.status,
    message: data?.message,

    // States
    isLoading,
    isError,
    error,

    // Actions
    refetch,
  };
};

// Parent-Connect Sessions Hook (raw rows; mapping to calendar Lessons
// happens in the calendar where the display timezone is available).
export const useParentConnectSessionsData = () => {
  const { data, isLoading, isError, error, refetch } =
    useParentConnectSessions();

  return {
    sessions: data ?? [],
    isLoading,
    isError,
    error,
    refetch,
  };
};

// Student Holidays Hook
export const useStudentHolidaysData = (
  eventStatus = 'active',
  userId: string,
  enabled = true
) => {
  const holidaysQuery = useStudentHolidays(eventStatus, userId, enabled);

  return {
    // Data
    holidays: holidaysQuery.data?.holidays ?? [],

    // States
    isLoading: holidaysQuery.isLoading,
    isError: holidaysQuery.isError,
    error: holidaysQuery.error,
    isSuccess: holidaysQuery.isSuccess,

    // Actions
    refetch: holidaysQuery.refetch,
  };
};

// Export query keys for external use
export { calendarKeys };
